import React, { useState, useMemo } from 'react';
import { Box, Form, Typography, Loader } from '@lucid/lego';
import type { FormField } from '@lucid/lego';
import { useStore } from '../../../store/store';
import {
  useGetCheckoutByShortcode,
  CHECKOUT_ERROR_CODES,
} from '../../../api/services/lupp/inventory/inventory';
import type { TransformedCheckoutDetail } from '../../../api/services/lupp/inventory/transformers.checkout';
import styles from './ConfigureShortCode.module.scss';

/** Shortcode must be exactly 6 alphanumeric characters */
const SHORTCODE_PATTERN = /^[A-Za-z0-9]{6}$/;

export type ConfigureShortCodeProps = {
  /** Callback when checkout data is successfully fetched */
  onCheckoutSuccess?: (data: TransformedCheckoutDetail, shortCode: string) => void;
  /** i18n labels for translations */
  labels?: Record<string, string>;
};

/**
 * Configure with short code tab content for creating vehicle quotations
 * Uses Form component with shortcode input and calls checkout API
 *
 * @component
 * @param props - Component props
 * @param props.onCheckoutSuccess - Callback when checkout data is fetched successfully
 * @param props.labels - i18n labels for translations
 * @returns JSX.Element
 *
 * @example
 * <ConfigureShortCode onCheckoutSuccess={(data, code) => navigateToCheckout(data, code)} />
 */
const ConfigureShortCode: React.FC<ConfigureShortCodeProps> = ({ onCheckoutSuccess, labels }) => {
  const theme = useStore((state) => state.theme);
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: getCheckoutByShortcode, isPending } = useGetCheckoutByShortcode();

  const formFields: FormField[] = useMemo(
    () => [
      {
        id: 'shortCodeInput',
        fieldType: 'text',
        name: 'shortCode',
        apiKey: 'shortCode',
        placeholder: labels?.['configurator.shortCode.placeholder'] ?? 'Enter Short Code',
        required: true,
        width: 'full',
        pattern: SHORTCODE_PATTERN,
        validationMessages: {
          required: labels?.['configurator.shortCode.required'] ?? 'Short code is required',
          pattern:
            labels?.['configurator.shortCode.patternError'] ??
            'Short code must be exactly 6 alphanumeric characters',
        },
      },
      {
        id: 'formButtons',
        fieldType: 'buttonGroup',
        align: 'right',
        buttons: [
          {
            id: 'submitButton',
            label: labels?.['configurator.shortCode.button'] ?? 'CONFIGURE VEHICLE',
            type: 'primary',
            size: 'medium',
            color: 'neutral',
            theme: invertedTheme,
            isFluid: true,
          },
        ],
      },
    ],
    [labels, invertedTheme]
  );

  const handleSuccess = async (data: unknown) => {
    setError(null);
    const formData = data as Record<string, unknown>;
    const shortCode = (formData.shortCode as string)?.trim();

    if (!shortCode) {
      setError(labels?.['configurator.shortCode.required'] ?? 'Short code is required');
      return;
    }

    try {
      const checkoutData = await getCheckoutByShortcode({ shortCode });
      onCheckoutSuccess?.(checkoutData, shortCode);
    } catch (err: unknown) {
      // Check for specific error codes from the checkout API
      if (
        err instanceof Error &&
        'errorCode' in err &&
        (err as Error & { errorCode: string }).errorCode ===
          CHECKOUT_ERROR_CODES.INVALID_VEHICLE_CONFIGURATION
      ) {
        setError(labels?.['configurator.shortCode.invalid'] ?? 'Invalid short code');
      } else {
        setError(
          labels?.['configurator.shortCode.error'] ??
            'Failed to fetch vehicle details. Please check the short code and try again.'
        );
      }
    }
  };

  const handleError = () => {
    setError(null);
  };

  return (
    <Box className={styles.configureShortCode} data-theme={invertedTheme}>
      <Box className={styles.content}>
        {isPending ? (
          <Box className={styles.loaderContainer}>
            <Loader size={48} />
            <Typography variant="body" size="medium" color="gray-3" theme={invertedTheme}>
              {labels?.['configurator.shortCode.loading'] ?? 'Loading vehicle details...'}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body" size="small" color="gray-3" theme={invertedTheme}>
              {labels?.['configurator.shortCode.description'] ??
                'Please use short code to configure the vehicle.'}
            </Typography>
            <Box mt={16} className={styles.formContainer}>
              <Form
                id="configure-short-code-form"
                theme={invertedTheme}
                formFields={formFields}
                onSuccess={handleSuccess}
                onError={handleError}
                isFluid
              />
              {error && (
                <Box mt={4}>
                  <Typography variant="body" size="small" color="error-1" theme={invertedTheme}>
                    {error}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ConfigureShortCode;
